import { useState, useEffect } from 'react'
import { Users, Plus, Edit, Shield, ShieldCheck, UserX, UserCheck, Eye, EyeOff, Zap, MessageCircle, History, ChevronDown, ChevronUp } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'
import Toast from '../../components/ui/Toast'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import usuariosService from '../../services/usuariosService'
import { formatPhone, stripDDI, addDDI } from '../../utils/masks'

function fmtData(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

export default function Usuarios({ organizadorId: organizadorIdProp }) {
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [mostrarInativos, setMostrarInativos] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [confirmToggle, setConfirmToggle] = useState(null)
  const [showLog2FA, setShowLog2FA] = useState(false)
  const [log2FA, setLog2FA] = useState([])
  const [loadingLog2FA, setLoadingLog2FA] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const organizadorId = organizadorIdProp ?? organizadorAtivo?.id

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    telefone: '',
    role: 'personalizado',
    modulos: []
  })

  // Permissões granulares de Ordem de Entrada (Premium)
  const [ordemEntradaPerm, setOrdemEntradaPerm] = useState({
    visualizar: true,
    editar_filtros: false,
    mover_manual: false,
    mover_manual_requer_justificativa: false,
  })

  const isPremiumFiltros = (organizadorAtivo?.produtos_premium || []).includes('filtros_baterias')

  const [modulosDisponiveis, setModulosDisponiveis] = useState([])

  const modulosBase = [
    { id: 'dashboard',         nome: 'Dashboard',            categoria: 'base' },
    { id: 'leilao-tempo-real', nome: 'Leilão em Tempo Real', categoria: 'base' },
    { id: 'clientes',          nome: 'Clientes',             categoria: 'base' },
    { id: 'leiloes',           nome: 'Leilões',              categoria: 'base' },
    { id: 'lotes',             nome: 'Lotes',                categoria: 'base' },
    { id: 'ordem-entrada',     nome: 'Ordem de Entrada',     categoria: 'base' },
    { id: 'catalogos',         nome: 'Catálogos',            categoria: 'base' },
    { id: 'mapas',             nome: 'Mapas',                categoria: 'base' },
    { id: 'relatorios',        nome: 'Relatórios',           categoria: 'base' },
    { id: 'recibos',           nome: 'Recibos',              categoria: 'base' },
    { id: 'configuracoes',     nome: 'Configurações',        categoria: 'base' },
  ]

  const modulosCadastros = [
    { id: 'comissoes',        nome: 'Comissões',          categoria: 'cadastros' },
    { id: 'leiloeiros',       nome: 'Leiloeiros',         categoria: 'cadastros' },
    { id: 'meios-pagamento',  nome: 'Meios de Pagamento', categoria: 'cadastros' },
    { id: 'tipos-animal',     nome: 'Tipos de Animal',    categoria: 'cadastros' },
    { id: 'racas',            nome: 'Raças',              categoria: 'cadastros' },
    { id: 'idades',           nome: 'Idades',             categoria: 'cadastros' },
    { id: 'pisteiros',        nome: 'Pisteiros',          categoria: 'cadastros' },
  ]

  const GESTAO_SUBMODULOS_IDS = ['gestao-contas-receber', 'gestao-contas-pagar', 'gestao-comprador-vendedor', 'gestao-categorias', 'gestao-formas-pagamento']

  const MAPAS_SUBITENS = [
    { id: 'mapas-fatura-comprador', nome: 'Fatura do Comprador' },
    { id: 'mapas-fatura-vendedor',  nome: 'Fatura do Vendedor' },
    { id: 'mapas-comprador',        nome: 'Mapa do Comprador' },
    { id: 'mapas-vendedor',         nome: 'Mapa do Vendedor' },
    { id: 'mapas-geral',            nome: 'Mapas Gerais' },
    { id: 'mapas-geral-defesas',    nome: 'Mapa Geral Defesas' },
    { id: 'mapas-geral-naomov',     nome: 'Mapa Geral Não Mov.' },
    { id: 'mapas-geral-vendedores', nome: 'Mapa Geral Vendedores' },
    { id: 'mapas-geral-compradores',nome: 'Mapa Geral Compradores' },
    { id: 'mapas-vendedor-defesa',  nome: 'Vendedor (Defesas)' },
  ]
  const MAPAS_SUBITEMS_IDS = MAPAS_SUBITENS.map(s => s.id)

  const RELATORIOS_SUBITENS = [
    { id: 'relatorios-autorizacao-gta',       nome: 'Autorização GTA' },
    { id: 'relatorios-balanco-vendas',         nome: 'Balanço de Vendas' },
    { id: 'relatorios-comissao-leiloeiro',     nome: 'Comissão Leiloeiro' },
    { id: 'relatorios-comissoes-cliente',      nome: 'Comissões por Cliente' },
    { id: 'relatorios-contrato-unificado',     nome: 'Contrato Unificado' },
    { id: 'relatorios-liberacao-curral',       nome: 'Liberação Curral' },
    { id: 'relatorios-fretes',                 nome: 'Fretes' },
    { id: 'relatorios-compradores',            nome: 'Compradores (Lista, Relação, Ranking)' },
    { id: 'relatorios-vendedores',             nome: 'Vendedores (Lista, Relação, Ranking)' },
    { id: 'relatorios-pisteiros',              nome: 'Pisteiros (Receita, Carteira)' },
    { id: 'relatorios-distribuicao-comissao',  nome: 'Distribuição Comissão' },
  ]
  const RELATORIOS_SUBITEMS_IDS = RELATORIOS_SUBITENS.map(s => s.id)

  const CONFIGURACOES_SUBITENS = [
    { id: 'configuracoes-catalogos',   nome: 'Catálogos (Leilão, Online, Pisteiro, Detalhado, Ficha)' },
    { id: 'configuracoes-contratos',   nome: 'Contratos' },
    { id: 'configuracoes-bancario',    nome: 'Dados Bancários' },
    { id: 'configuracoes-faturamento', nome: 'Faturamento' },
    { id: 'configuracoes-mapas',       nome: 'Mapas' },
    { id: 'configuracoes-relatorios',  nome: 'Relatórios' },
    { id: 'configuracoes-usuarios',    nome: 'Usuários' },
  ]
  const CONFIGURACOES_SUBITEMS_IDS = CONFIGURACOES_SUBITENS.map(s => s.id)

  const modulosPremium = [
    { id: 'portaria',                  nome: 'Portaria',                         categoria: 'premium', produto: 'portaria' },
    { id: 'gestao-contas-receber',     nome: 'Contas a Receber + Relatório',     categoria: 'premium', produto: 'gestao_financeira', grupo: 'gestao' },
    { id: 'gestao-contas-pagar',       nome: 'Contas a Pagar + Relatório',       categoria: 'premium', produto: 'gestao_financeira', grupo: 'gestao' },
    { id: 'gestao-comprador-vendedor', nome: 'Comprador x Vendedor + Relatório', categoria: 'premium', produto: 'gestao_financeira', grupo: 'gestao' },
    { id: 'gestao-categorias',         nome: 'Categorias',                       categoria: 'premium', produto: 'gestao_financeira', grupo: 'gestao' },
    { id: 'gestao-formas-pagamento',   nome: 'Formas de Pagamento',              categoria: 'premium', produto: 'gestao_financeira', grupo: 'gestao' },
    { id: 'socios',                    nome: 'Gestão de Sócios',                 categoria: 'premium', produto: 'gestao_socios' },
    { id: 'inteligencia-pecuaria',     nome: 'Inteligência Pecuária',            categoria: 'premium', produto: 'inteligencia_pecuaria' },
  ]

  useEffect(() => {
    if (organizadorId) {
      loadUsuarios()
      loadModulosDisponiveis()
    }
  }, [debouncedSearchTerm, organizadorId, mostrarInativos])

  const loadModulosDisponiveis = () => {
    if (!organizadorAtivo) { setModulosDisponiveis(modulosBase); return }
    let modulos = [...modulosBase, ...modulosCadastros]
    if (organizadorAtivo.produtos_premium?.length > 0) {
      modulos = [...modulos, ...modulosPremium.filter(m => organizadorAtivo.produtos_premium.includes(m.produto))]
    }
    setModulosDisponiveis(modulos)
  }

  const loadUsuarios = async () => {
    if (!organizadorId) return
    try {
      setLoading(true)
      const data = await usuariosService.getByOrganizador(organizadorId, debouncedSearchTerm, mostrarInativos)
      setUsuarios(data || [])
    } catch (err) {
      error('Erro ao carregar usuários')
      setUsuarios([])
    } finally {
      setLoading(false)
    }
  }

  const handleNew = () => {
    setEditingUser(null)
    const defaultModulos = modulosDisponiveis.flatMap(m => {
      if (m.id === 'mapas') return MAPAS_SUBITEMS_IDS
      if (m.id === 'relatorios') return RELATORIOS_SUBITEMS_IDS
      if (m.id === 'configuracoes') return CONFIGURACOES_SUBITEMS_IDS
      return [m.id]
    })
    setFormData({ nome: '', email: '', senha: '', telefone: '', role: 'personalizado', modulos: defaultModulos })
    setOrdemEntradaPerm({ visualizar: true, editar_filtros: false, mover_manual: false, mover_manual_requer_justificativa: false })
    setShowModal(true)
  }

  const handleEdit = (usuario) => {
    setEditingUser(usuario)
    let modulos = usuario.modulos_acesso || []
    if (modulos.includes('gestao')) {
      modulos = [...modulos.filter(id => id !== 'gestao'), ...GESTAO_SUBMODULOS_IDS]
    }
    if (modulos.includes('mapas')) {
      modulos = [...modulos.filter(id => id !== 'mapas'), ...MAPAS_SUBITEMS_IDS]
    }
    if (modulos.includes('relatorios')) {
      modulos = [...modulos.filter(id => id !== 'relatorios'), ...RELATORIOS_SUBITEMS_IDS]
    }
    if (modulos.includes('configuracoes')) {
      modulos = [...modulos.filter(id => id !== 'configuracoes'), ...CONFIGURACOES_SUBITEMS_IDS]
    }
    // Extrair permissões de ordem_entrada se existir como objeto
    const oeObj = modulos.find(m => m?.modulo === 'ordem_entrada')
    if (oeObj) {
      setOrdemEntradaPerm({
        visualizar: oeObj.visualizar ?? true,
        editar_filtros: oeObj.editar_filtros ?? false,
        mover_manual: oeObj.mover_manual ?? false,
        mover_manual_requer_justificativa: oeObj.mover_manual_requer_justificativa ?? false,
      })
    } else {
      // fallback: se tem 'ordem-entrada' simples, mapear para visualizar apenas
      const temOE = modulos.includes('ordem-entrada') || modulos.includes('ordem_entrada')
      setOrdemEntradaPerm({ visualizar: temOE, editar_filtros: temOE, mover_manual: temOE, mover_manual_requer_justificativa: false })
    }
    // Remover o objeto de ordem_entrada do array de strings
    const modulosSemOE = modulos.filter(m => typeof m === 'string' && m !== 'ordem-entrada' && m !== 'ordem_entrada')
    setFormData({ nome: usuario.name || '', email: usuario.email || '', senha: '', telefone: formatPhone(stripDDI(usuario.telefone || '')), role: usuario.permission_level || 'personalizado', modulos: modulosSemOE })
    setShowModal(true)
  }

  const handleToggleLog2FA = async () => {
    const novoEstado = !showLog2FA
    setShowLog2FA(novoEstado)
    if (novoEstado && log2FA.length === 0) {
      setLoadingLog2FA(true)
      try {
        const data = await usuariosService.getLog2FA(organizadorId)
        setLog2FA(data)
      } catch {
        error('Erro ao carregar histórico de verificação.')
      } finally {
        setLoadingLog2FA(false)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!organizadorId) { error('Nenhum organizador selecionado'); return }
    try {
      // Construir modulos_acesso: strings + objeto ordem_entrada se premium e personalizado
      let modulosFinais = [...formData.modulos]
      if (formData.role === 'personalizado') {
        // Incluir permissão de ordem_entrada como objeto (suporte legado e novo)
        if (ordemEntradaPerm.visualizar || ordemEntradaPerm.editar_filtros || ordemEntradaPerm.mover_manual) {
          if (isPremiumFiltros) {
            modulosFinais = [...modulosFinais.filter(m => m !== 'ordem-entrada' && m !== 'ordem_entrada'), { modulo: 'ordem_entrada', ...ordemEntradaPerm }]
          } else {
            modulosFinais = [...modulosFinais.filter(m => m !== 'ordem-entrada' && m !== 'ordem_entrada'), 'ordem-entrada']
          }
        }
      }
      const userData = {
        name: formData.nome,
        email: formData.email,
        password: formData.senha,
        telefone: addDDI(formData.telefone),
        permission_level: formData.role,
        modulos_acesso: modulosFinais
      }
      if (editingUser) {
        await usuariosService.update(organizadorId, editingUser.id, userData)
        success('Usuário atualizado com sucesso!')
      } else {
        await usuariosService.create(organizadorId, userData)
        success('Usuário criado com sucesso!')
      }
      setShowModal(false)
      await loadUsuarios()
    } catch (err) {
      error(err.response?.data?.detail || 'Erro ao salvar usuário')
    }
  }

  const confirmToggleAction = async () => {
    if (!confirmToggle) return
    try {
      const res = await usuariosService.toggleStatus(organizadorId, confirmToggle.id)
      success(res.message)
      await loadUsuarios()
    } catch (err) {
      error(err.response?.data?.detail || 'Erro ao alterar status')
    }
    setConfirmToggle(null)
  }

  const MODULOS_GRANULARES = ['clientes', 'leiloes', 'lotes']

  const getModuloGranularNivel = (moduloId) => {
    if (formData.modulos.includes(moduloId)) return 'completo'
    if (formData.modulos.includes(`${moduloId}-visualizar`)) return 'visualizar'
    return 'sem'
  }

  const handleModuloGranularChange = (moduloId, nivel) => {
    setFormData(prev => {
      const sem = prev.modulos.filter(id => id !== moduloId && id !== `${moduloId}-visualizar`)
      if (nivel === 'visualizar') return { ...prev, modulos: [...sem, `${moduloId}-visualizar`] }
      if (nivel === 'completo') return { ...prev, modulos: [...sem, moduloId] }
      return { ...prev, modulos: sem }
    })
  }

  const handleModuloChange = (moduloId) => {
    setFormData(prev => ({
      ...prev,
      modulos: prev.modulos.includes(moduloId)
        ? prev.modulos.filter(id => id !== moduloId)
        : [...prev.modulos, moduloId]
    }))
  }

  const perfilLabel = (level) => {
    const map = { super_admin: 'Super Admin', admin: 'Administrador', manejo: 'Manejo', transmissao: 'Transmissão', escritorio: 'Escritório' }
    return map[level] || 'Personalizado'
  }

  const perfilColor = (level) => {
    if (level === 'admin' || level === 'super_admin') return 'bg-red-100 text-red-800'
    if (level === 'escritorio') return 'bg-purple-100 text-purple-800'
    if (level === 'manejo') return 'bg-green-100 text-green-800'
    if (level === 'transmissao') return 'bg-orange-100 text-orange-800'
    return 'bg-blue-100 text-blue-800'
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${organizadorIdProp ? 'py-12' : 'min-h-screen'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue" />
      </div>
    )
  }

  return (
    <>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} duration={toast.duration} onClose={() => hideToast(toast.id)} />
      ))}

      <ConfirmDialog
        isOpen={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        onConfirm={confirmToggleAction}
        title={confirmToggle?.is_active ? 'Inativar Usuário' : 'Reativar Usuário'}
        message={
          confirmToggle?.is_active
            ? `Tem certeza que deseja inativar "${confirmToggle?.name}"? O usuário perderá acesso ao sistema imediatamente.`
            : `Deseja reativar "${confirmToggle?.name}"? O usuário voltará a ter acesso ao sistema.`
        }
        confirmText={confirmToggle?.is_active ? 'Inativar' : 'Reativar'}
        cancelText="Cancelar"
        type={confirmToggle?.is_active ? 'danger' : 'info'}
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Usuários</h1>
            <p className="text-xs sm:text-sm text-gray-500">Gerencie os usuários do organizador</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleToggleLog2FA} className="btn-secondary flex items-center">
              <History className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Histórico </span>2FA
              {showLog2FA ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </button>
            <button onClick={handleNew} className="btn-primary">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Novo </span>Usuário
            </button>
          </div>
        </div>

        {showLog2FA && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {loadingLog2FA ? (
              <div className="p-4 text-sm text-gray-500">Carregando...</div>
            ) : log2FA.length === 0 ? (
              <div className="p-4 text-sm text-gray-400">Nenhuma verificação de código registrada ainda.</div>
            ) : (
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Usuário</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Código</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Resultado</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">IP</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Dispositivo</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Data/hora</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {log2FA.map(l => (
                    <tr key={l.id}>
                      <td className="px-4 py-2">
                        <div className="text-gray-900">{l.usuario_nome}</div>
                        <div className="text-xs text-gray-400">{l.usuario_email}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-gray-700">{l.codigo_digitado}</td>
                      <td className="px-4 py-2">
                        {l.sucesso
                          ? <span className="text-green-700 text-xs font-medium">Válido</span>
                          : <span className="text-red-600 text-xs font-medium">Inválido</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{l.ip || '—'}</td>
                      <td className="px-4 py-2 text-gray-500 truncate max-w-[220px]" title={l.user_agent}>{l.user_agent || '—'}</td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fmtData(l.criado_em)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Buscar usuários..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-rematei flex-1 min-w-[200px]"
          />
          <button
            onClick={() => setMostrarInativos(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              mostrarInativos
                ? 'bg-gray-700 text-white border-gray-700'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {mostrarInativos ? <EyeOff size={15} /> : <Eye size={15} />}
            {mostrarInativos ? 'Ocultar inativos' : 'Ver inativos'}
          </button>
        </div>

        {/* Lista */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {usuarios.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Users className="w-12 sm:w-16 h-12 sm:h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Nenhum usuário encontrado</h3>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm ? 'Tente ajustar sua busca' : 'Comece criando seu primeiro usuário'}
              </p>
              <button onClick={handleNew} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" /> Novo Usuário
              </button>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-200">
                {usuarios.map((usuario) => (
                  <div key={usuario.id} className={`p-4 ${!usuario.is_active ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 text-sm truncate">{usuario.name}</div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">{usuario.email}</div>
                        <div className="text-xs text-gray-400 mt-0.5">Último login: {usuario.last_login ? fmtData(usuario.last_login) : 'nunca acessou'}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {usuario.is_active && (
                          <button onClick={() => handleEdit(usuario)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setConfirmToggle(usuario)}
                          className={`p-1.5 rounded ${usuario.is_active ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                          title={usuario.is_active ? 'Inativar' : 'Reativar'}>
                          {usuario.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${perfilColor(usuario.permission_level)}`}>
                        {usuario.permission_level === 'admin' || usuario.permission_level === 'super_admin'
                          ? <ShieldCheck className="w-3 h-3 mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
                        {perfilLabel(usuario.permission_level)}
                      </span>
                      {usuario.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Inativo
                        </span>
                      )}
                      {usuario.tem_dispositivo_confiavel ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          <ShieldCheck className="w-3 h-3" /> 2FA verificado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                          <Shield className="w-3 h-3" /> Nunca verificado
                        </span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">{usuario.modulos_acesso?.length || 0} módulos</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <table className="hidden sm:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Perfil</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">2FA</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Módulos</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className={`hover:bg-gray-50 ${!usuario.is_active ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{usuario.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">Último login: {usuario.last_login ? fmtData(usuario.last_login) : 'nunca acessou'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usuario.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${perfilColor(usuario.permission_level)}`}>
                          {usuario.permission_level === 'admin' || usuario.permission_level === 'super_admin'
                            ? <ShieldCheck className="w-3 h-3 mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
                          {perfilLabel(usuario.permission_level)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {usuario.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Ativo
                          </span>
                        ) : (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Inativo
                            </span>
                            {usuario.inativado_em && (
                              <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">
                                {fmtData(usuario.inativado_em)}
                                {usuario.inativado_por && <span> · {usuario.inativado_por}</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {usuario.tem_dispositivo_confiavel ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              <ShieldCheck className="w-3 h-3" /> Verificado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                              <Shield className="w-3 h-3" /> Nunca
                            </span>
                          )}
                          {usuario.telefone && (
                            <MessageCircle className="w-3.5 h-3.5 text-green-600" title="WhatsApp cadastrado" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {usuario.modulos_acesso?.length || 0} módulo(s)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {usuario.is_active && (
                            <button onClick={() => handleEdit(usuario)} className="text-blue-600 hover:text-blue-700 p-1 rounded" title="Editar">
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => setConfirmToggle(usuario)}
                            className={`p-1 rounded ${usuario.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}
                            title={usuario.is_active ? 'Inativar' : 'Reativar'}>
                            {usuario.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-lg max-w-lg w-full">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                    <input type="text" required value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} className="input-rematei" placeholder="Nome completo" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-rematei" placeholder="email@exemplo.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                    <input type="text" value={formData.telefone} onChange={(e) => setFormData({...formData, telefone: formatPhone(e.target.value)})} className="input-rematei" placeholder="(00) 00000-0000" />
                    <p className="text-xs text-gray-400 mt-1">Usado para receber o código de verificação (2FA) por WhatsApp. Só DDD + número — o 55 é adicionado automaticamente.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha *'}
                    </label>
                    <input type="password" required={!editingUser} minLength="6" value={formData.senha} onChange={(e) => setFormData({...formData, senha: e.target.value})} className="input-rematei" placeholder="Mínimo 6 caracteres" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Perfil</label>
                    <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="input-rematei">
                      <option value="admin">Administrador</option>
                      <option value="manejo">Manejo</option>
                      <option value="transmissao">Transmissão</option>
                      <option value="escritorio">Escritório</option>
                      <option value="personalizado">Personalizado</option>
                    </select>
                  </div>

                  {formData.role === 'personalizado' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Módulos Permitidos</label>
                      <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Módulos Base</h4>
                          <div className="space-y-2">
                            {modulosDisponiveis.filter(m => m.categoria === 'base').map((modulo) => (
                              MODULOS_GRANULARES.includes(modulo.id) ? (
                                <div key={modulo.id} className="flex items-center justify-between py-0.5">
                                  <span className="text-sm text-gray-700">{modulo.nome}</span>
                                  <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                                    {[
                                      { valor: 'sem', label: 'Sem acesso' },
                                      { valor: 'visualizar', label: 'Visualizar' },
                                      { valor: 'completo', label: 'Completo' },
                                    ].map(({ valor, label }) => {
                                      const ativo = getModuloGranularNivel(modulo.id) === valor
                                      return (
                                        <button key={valor} type="button" onClick={() => handleModuloGranularChange(modulo.id, valor)}
                                          className={`px-2.5 py-1 transition-colors ${ativo
                                            ? valor === 'sem' ? 'bg-gray-200 text-gray-800'
                                              : valor === 'visualizar' ? 'bg-blue-100 text-blue-800'
                                              : 'bg-green-100 text-green-800'
                                            : 'bg-white text-gray-400 hover:bg-gray-50'}`}
                                        >{label}</button>
                                      )
                                    })}
                                  </div>
                                </div>
                              ) : modulo.id === 'mapas' ? (
                                <div key="mapas" className="border border-cyan-200 rounded-lg p-2.5 bg-cyan-50/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-cyan-700">Mapas</span>
                                    <div className="flex gap-2 text-[11px]">
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: [...prev.modulos.filter(id => !MAPAS_SUBITEMS_IDS.includes(id)), ...MAPAS_SUBITEMS_IDS] }))}
                                        className="text-cyan-600 hover:text-cyan-800 font-medium">Marcar todos</button>
                                      <span className="text-gray-300">|</span>
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: prev.modulos.filter(id => !MAPAS_SUBITEMS_IDS.includes(id)) }))}
                                        className="text-cyan-600 hover:text-cyan-800 font-medium">Desmarcar</button>
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 ml-1">
                                    {MAPAS_SUBITENS.map((sub) => (
                                      <label key={sub.id} className="flex items-center">
                                        <input type="checkbox" checked={formData.modulos.includes(sub.id)} onChange={() => handleModuloChange(sub.id)} className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded" />
                                        <span className="ml-2 text-sm text-cyan-600">{sub.nome}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ) : modulo.id === 'relatorios' ? (
                                <div key="relatorios" className="border border-rose-200 rounded-lg p-2.5 bg-rose-50/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-rose-700">Relatórios</span>
                                    <div className="flex gap-2 text-[11px]">
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: [...prev.modulos.filter(id => !RELATORIOS_SUBITEMS_IDS.includes(id)), ...RELATORIOS_SUBITEMS_IDS] }))}
                                        className="text-rose-600 hover:text-rose-800 font-medium">Marcar todos</button>
                                      <span className="text-gray-300">|</span>
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: prev.modulos.filter(id => !RELATORIOS_SUBITEMS_IDS.includes(id)) }))}
                                        className="text-rose-600 hover:text-rose-800 font-medium">Desmarcar</button>
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 ml-1">
                                    {RELATORIOS_SUBITENS.map((sub) => (
                                      <label key={sub.id} className="flex items-center">
                                        <input type="checkbox" checked={formData.modulos.includes(sub.id)} onChange={() => handleModuloChange(sub.id)} className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded" />
                                        <span className="ml-2 text-sm text-rose-600">{sub.nome}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ) : modulo.id === 'configuracoes' ? (
                                <div key="configuracoes" className="border border-slate-200 rounded-lg p-2.5 bg-slate-50/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-slate-700">Configurações</span>
                                    <div className="flex gap-2 text-[11px]">
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: [...prev.modulos.filter(id => !CONFIGURACOES_SUBITEMS_IDS.includes(id)), ...CONFIGURACOES_SUBITEMS_IDS] }))}
                                        className="text-slate-600 hover:text-slate-800 font-medium">Marcar todos</button>
                                      <span className="text-gray-300">|</span>
                                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, modulos: prev.modulos.filter(id => !CONFIGURACOES_SUBITEMS_IDS.includes(id)) }))}
                                        className="text-slate-600 hover:text-slate-800 font-medium">Desmarcar</button>
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 ml-1">
                                    {CONFIGURACOES_SUBITENS.map((sub) => (
                                      <label key={sub.id} className="flex items-center">
                                        <input type="checkbox" checked={formData.modulos.includes(sub.id)} onChange={() => handleModuloChange(sub.id)} className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded" />
                                        <span className="ml-2 text-sm text-slate-600">{sub.nome}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <label key={modulo.id} className="flex items-center">
                                  <input type="checkbox" checked={formData.modulos.includes(modulo.id)} onChange={() => handleModuloChange(modulo.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{modulo.nome}</span>
                                </label>
                              )
                            ))}
                          </div>
                        </div>

                        {modulosDisponiveis.some(m => m.categoria === 'cadastros') && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cadastros</h4>
                            <div className="space-y-2">
                              {modulosDisponiveis.filter(m => m.categoria === 'cadastros').map((modulo) => (
                                <label key={modulo.id} className="flex items-center">
                                  <input type="checkbox" checked={formData.modulos.includes(modulo.id)} onChange={() => handleModuloChange(modulo.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{modulo.nome}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {modulosDisponiveis.some(m => m.categoria === 'premium') && (
                          <div>
                            <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2 flex items-center">
                              <span className="w-2 h-2 bg-amber-500 rounded-full mr-2" />
                              Módulos Premium
                            </h4>
                            <div className="space-y-2">
                              {modulosDisponiveis.filter(m => m.categoria === 'premium' && !m.grupo).map((modulo) => (
                                <label key={modulo.id} className="flex items-center">
                                  <input type="checkbox" checked={formData.modulos.includes(modulo.id)} onChange={() => handleModuloChange(modulo.id)} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-amber-700 font-medium">{modulo.nome}</span>
                                </label>
                              ))}
                              {modulosDisponiveis.some(m => m.grupo === 'gestao') && (
                                <div className="border border-amber-200 rounded-lg p-2.5 bg-amber-50/40">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-amber-700">Gestão Financeira</span>
                                    <div className="flex gap-2 text-[11px]">
                                      <button type="button" onClick={() => {
                                        const ids = modulosDisponiveis.filter(m => m.grupo === 'gestao').map(m => m.id)
                                        setFormData(prev => ({ ...prev, modulos: [...prev.modulos.filter(id => !ids.includes(id)), ...ids] }))
                                      }} className="text-amber-600 hover:text-amber-800 font-medium">Marcar todos</button>
                                      <span className="text-gray-300">|</span>
                                      <button type="button" onClick={() => {
                                        const ids = modulosDisponiveis.filter(m => m.grupo === 'gestao').map(m => m.id)
                                        setFormData(prev => ({ ...prev, modulos: prev.modulos.filter(id => !ids.includes(id)) }))
                                      }} className="text-amber-600 hover:text-amber-800 font-medium">Desmarcar</button>
                                    </div>
                                  </div>
                                  <div className="space-y-1.5 ml-1">
                                    {modulosDisponiveis.filter(m => m.grupo === 'gestao').map((modulo) => (
                                      <label key={modulo.id} className="flex items-center">
                                        <input type="checkbox" checked={formData.modulos.includes(modulo.id)} onChange={() => handleModuloChange(modulo.id)} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" />
                                        <span className="ml-2 text-sm text-amber-600">{modulo.nome}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Permissões de Ordem de Entrada (Premium) */}
                  {formData.role === 'personalizado' && isPremiumFiltros && (
                    <div className="border border-purple-200 rounded-xl p-4 bg-purple-50/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap size={15} className="text-purple-600" />
                        <h4 className="text-sm font-semibold text-purple-800">Ordem de Entrada — Permissões</h4>
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">Premium</span>
                      </div>
                      <div className="space-y-2.5">
                        {[
                          { key: 'visualizar', label: 'Visualizar ordem de entrada', desc: 'Pode ver a lista, sem editar' },
                          { key: 'editar_filtros', label: 'Editar e aplicar filtros automáticos', desc: 'Criar/editar templates e gerar ordem' },
                          { key: 'mover_manual', label: 'Mover lotes manualmente', desc: 'Drag & drop na ordem gerada' },
                          { key: 'mover_manual_requer_justificativa', label: 'Exigir justificativa ao mover', desc: 'Obrigatório informar motivo ao mover' },
                        ].map(({ key, label, desc }) => (
                          <label key={key} className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ordemEntradaPerm[key]}
                              onChange={e => setOrdemEntradaPerm(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="mt-0.5 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <div>
                              <div className="text-sm text-gray-800 font-medium">{label}</div>
                              <div className="text-xs text-gray-500">{desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.role !== 'personalizado' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Módulos incluídos neste perfil:</h4>
                      {formData.role === 'admin' && <p className="text-xs text-blue-700">Acesso total ao sistema, incluindo gerenciamento de usuários e configurações.</p>}
                      {formData.role === 'escritorio' && (
                        <>
                          <p className="text-xs text-blue-600 mb-2">Acesso a todos os módulos, exceto:</p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Usuários</span>
                        </>
                      )}
                      {formData.role === 'manejo' && (
                        <div className="flex flex-wrap gap-1.5">
                          {[{ nome: 'Dashboard' }, { nome: 'Clientes', nivel: 'Completo' }, { nome: 'Lotes', nivel: 'Completo' }, { nome: 'Relatórios' }].map((mod) => (
                            <span key={mod.nome} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {mod.nome}
                              {mod.nivel && <span className="px-1 py-0 rounded bg-green-200 text-green-700 text-[10px] font-semibold">{mod.nivel}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                      {formData.role === 'transmissao' && (
                        <div className="flex flex-wrap gap-1.5">
                          {[{ nome: 'Catálogos', nivel: 'Transmissão' }].map((mod) => (
                            <span key={mod.nome} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                              {mod.nome}
                              {mod.nivel && <span className="px-1 py-0 rounded bg-orange-200 text-orange-700 text-[10px] font-semibold">{mod.nivel}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancelar</button>
                    <button type="submit" className="btn-primary">{editingUser ? 'Atualizar' : 'Criar'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
